import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface BuildVersionInfo {
  appName: string;
  version: string;
  buildId: string;
  commit: string;
  environment: string;
  builtAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class BuildVersionService {
  private readonly http = inject(HttpClient);
  private readonly currentBuildSubject = new BehaviorSubject<BuildVersionInfo | null>(null);
  private readonly availableUpdateSubject = new BehaviorSubject<BuildVersionInfo | null>(null);
  private pollTimer: number | null = null;
  private initialized = false;

  readonly currentBuild$ = this.currentBuildSubject.asObservable();
  readonly availableUpdate$ = this.availableUpdateSubject.asObservable();

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const current = await this.fetchVersionInfo().catch(() => null);
    if (current) {
      this.currentBuildSubject.next(current);
    }
    this.startPolling();
  }

  reloadPage(): void {
    window.location.reload();
  }

  private startPolling(): void {
    if (this.pollTimer != null) {
      return;
    }
    this.pollTimer = window.setInterval(() => {
      void this.checkForUpdate();
    }, 60_000);
  }

  private async checkForUpdate(): Promise<void> {
    const latest = await this.fetchVersionInfo().catch(() => null);
    const current = this.currentBuildSubject.value;
    if (!latest) {
      return;
    }
    if (!current) {
      this.currentBuildSubject.next(latest);
      return;
    }
    if (this.sameBuild(current, latest)) {
      return;
    }
    if (!this.availableUpdateSubject.value || !this.sameBuild(this.availableUpdateSubject.value, latest)) {
      this.availableUpdateSubject.next(latest);
    }
  }

  private async fetchVersionInfo(): Promise<BuildVersionInfo> {
    return firstValueFrom(
      this.http.get<BuildVersionInfo>(`./assets/version.json?t=${Date.now()}`)
    );
  }

  private sameBuild(left: BuildVersionInfo, right: BuildVersionInfo): boolean {
    return left.version === right.version
      && left.buildId === right.buildId
      && left.commit === right.commit;
  }
}
